import {
  useMemo,
  useState,
} from "react";
import { readCmtsSession } from "@cmts/auth/authStore.js";

import {
  useSearchParams,
} from "react-router-dom";

import {
  Button,
  PageContainer,
  type ComponentDiagnostic,
} from "@swot/base-components";

import type {
  EnterpriseTask,
} from "@swot/swot-components";

import {
  FiPlusCircle,
} from "react-icons/fi";

import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridFilterControl,
} from "@components/data-grid/index.js";

import {
  CmtsPageHintPanel,
} from "@components/hints/index.js";

import {
  CmtsEnterpriseModal,
} from "@components/CmtsEnterpriseModal.js";

import {
  CmtsEnterpriseForm,
  type CmtsFormField,
} from "@components/forms/index.js";

type TaskRow =
  EnterpriseTask &
  Record<string, unknown>;

type CmtsFormOption = {
  readonly value: string;
  readonly label: string;
};

type ModalMode =
  | "create"
  | "reassign"
  | "comment"
  | "snooze"
  | null;

type TaskActionCardProps = {
  icon: string;
  title: string;
  description: string;
  disabled?: boolean | undefined;
  onClick: () => void;
};

function text(
  row: Record<string, unknown>,
  keys: readonly string[],
  fallback = "",
): string {
  for (const key of keys) {
    const value = row[key];

    if (
      value !== undefined &&
      value !== null &&
      String(value).trim()
    ) {
      return String(value);
    }
  }

  return fallback;
}

function taskId(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    ["id", "taskId"],
  );
}

function linkedId(
  row: Record<string, unknown>,
): string {
  return text(row, [
    "complaintId",
    "parentComplaintId",
    "linkedComplaintId",
    "linkedEntityId",
    "sourceEntityId",
    "sourceRecordId",
    "entityId",
  ]);
}

function normalizeLinkedId(
  value: unknown,
): string {
  return String(value ?? "")
    .trim()
    .replace(/[{}]/g, "")
    .toLowerCase();
}
function taskStatus(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    [
      "taskStatus",
      "status",
    ],
    "OPEN",
  )
    .trim()
    .toUpperCase();
}
function taskStatusClass(
  status: string,
): string {
  const normalized = status
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  const classes: Record<string, string> = {
    OPEN: "cmts-status-chip--open",
    ASSIGNED: "cmts-status-chip--assigned",
    ACKNOWLEDGED: "cmts-status-chip--acknowledged",
    IN_PROGRESS: "cmts-status-chip--in-progress",
    PENDING: "cmts-status-chip--pending",
    SNOOZED: "cmts-status-chip--snoozed",
    OVERDUE: "cmts-status-chip--overdue",
    COMPLETED: "cmts-status-chip--completed",
    DONE: "cmts-status-chip--completed",
    CLOSED: "cmts-status-chip--closed",
    CANCELLED: "cmts-status-chip--cancelled",
    ARCHIVED: "cmts-status-chip--archived",
  };

  return (
    classes[normalized] ??
    "cmts-status-chip--default"
  );
}
function isClosed(
  row: Record<string, unknown>,
): boolean {
  return [
    "DONE",
    "COMPLETED",
    "CLOSED",
    "CANCELLED",
    "ARCHIVED",
  ].includes(taskStatus(row));
}

function capability(
  row: Record<string, unknown>,
  key: string,
  fallback = true,
): boolean {
  const value = row[key];

  return typeof value === "boolean"
    ? value
    : fallback;
}

function normalizeUrlFilterValue(
  value: string,
): string {
  return decodeURIComponent(value)
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
}

function initialGridStateFromUrl(
  params: URLSearchParams,
): {
  filters: Record<string, string>;
  searchText: string;
} {
  const filters:
    Record<string, string> = {};

  const searchText =
    params.get("q") ??
    params.get("search") ??
    "";

  const alias:
    Record<string, string> = {
    owner: "ownerId",
    assignee: "ownerId",
    statusGroup: "status",
    type: "linkedEntityType",
    module: "sourceModule",
  };

  params.forEach(
    (value, key) => {
      if (
        !["q", "search"].includes(
          key,
        ) &&
        value
      ) {
        filters[alias[key] ?? key] =
          normalizeUrlFilterValue(
            value,
          );
      }
    },
  );

  return {
    filters,
    searchText,
  };
}

function formatTaskDate(
  row: Record<string, unknown>,
  keys: readonly string[],
): string {
  const value = text(row, keys);

  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(
    undefined,
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  );
}

function TaskActionCard({
  icon,
  title,
  description,
  disabled = false,
  onClick,
}: TaskActionCardProps) {
  return (
    <button
      type="button"
      className="cmts-notification-action-option"
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>

        <small>
          {description}
        </small>
      </span>

      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

const taskFilters:
  readonly CmtsGridFilterControl<
    Record<string, unknown>
  >[] = [
    {
      id: "status",
      label: "Status",
      columnId: "status",
      operator: "contains",
      placeholder: "All statuses",
    },
    {
      id: "priority",
      label: "Priority",
      columnId: "priority",
      operator: "contains",
      placeholder: "All priorities",
    },
    {
      id: "ownerId",
      label: "Owner",
      columnId: "ownerId",
      operator: "contains",
      placeholder: "All owners",
    },
    {
      id: "linkedEntityType",
      label: "Source",
      columnId: "linkedEntityType",
      operator: "contains",
      placeholder: "All sources",
    },
  ];

const DEFAULT_TASK_TYPES: readonly CmtsFormOption[] = [
  { value: "WORKFLOW_ACTION", label: "Workflow Action" },
  { value: "RCA_INVESTIGATION", label: "RCA Investigation" },
  { value: "CAPA_IMPLEMENTATION", label: "CAPA Implementation" },
  { value: "VERIFICATION", label: "Verification" },
  { value: "MESSAGE_FOLLOW_UP", label: "Message Follow-up" },
  { value: "ESCALATION_REVIEW", label: "Escalation Review" },
  { value: "GENERAL_TASK", label: "General Task" },
];
const DEFAULT_LINKED_ENTITY_TYPES: readonly CmtsFormOption[] = [
  { value: "Complaint", label: "Complaint" },
  { value: "RCA", label: "RCA" },
  { value: "CAPA", label: "CAPA" },
  { value: "Verification", label: "Verification" },
  { value: "Message", label: "Message" },
];
const DEFAULT_PRIORITIES: readonly CmtsFormOption[] = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
  { value: "CRITICAL", label: "Critical" },
];
const DEFAULT_CONFIDENTIALITIES: readonly CmtsFormOption[] = [
  { value: "STANDARD", label: "Standard / Internal" },
  { value: "RESTRICTED", label: "Restricted" },
  { value: "CONFIDENTIAL", label: "Confidential" },
];
const DEFAULT_ROLES: readonly CmtsFormOption[] = [
  { value: "INVESTIGATOR", label: "Investigator" },
  { value: "CAPA_OWNER", label: "CAPA Owner" },
  { value: "VERIFIER", label: "Verifier" },
  { value: "COMPLAINT_OWNER", label: "Complaint Owner" },
  { value: "QUALITY_MANAGER", label: "Quality Manager" },
  { value: "PLATFORM_ADMIN", label: "Platform Admin" },
];
function buildCreateFields(
  priorityOptions:
    readonly CmtsFormOption[],
  userOptions:
    readonly CmtsFormOption[],
  complaintOptions:
    readonly CmtsFormOption[],
  taskTypeOptions: readonly CmtsFormOption[],
  linkedEntityTypeOptions: readonly CmtsFormOption[],
  roleOptions: readonly CmtsFormOption[],
  confidentialityOptions: readonly CmtsFormOption[],
): readonly CmtsFormField[] {
  const session = readCmtsSession();
  const resolvedTaskTypes = taskTypeOptions.length > 0 ? taskTypeOptions : DEFAULT_TASK_TYPES;
  const resolvedLinkedEntityTypes = linkedEntityTypeOptions.length > 0 ? linkedEntityTypeOptions : DEFAULT_LINKED_ENTITY_TYPES;
  const resolvedPriorities = priorityOptions.length > 0 ? priorityOptions : DEFAULT_PRIORITIES;
  const resolvedConfidentialities = confidentialityOptions.length > 0 ? confidentialityOptions : DEFAULT_CONFIDENTIALITIES;
  const resolvedRoles = roleOptions.length > 0 ? roleOptions : DEFAULT_ROLES;
  const resolvedUsers = userOptions.length > 0 ? userOptions : [
    { value: session?.userId ?? "current-user", label: session?.displayName ?? session?.email ?? "Current User" }
  ];

  return [
    {
      name: "title",
      label: "Task title",
      required: true,
      minLength: 4,
      placeholder:
        "Example: Verify CAPA evidence",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea" as const,
    },
    {
      name: "taskTypeCode",
      label: "Task type code",
      type: "select" as const,
      required: true,
      options: resolvedTaskTypes,
      placeholder: "Select task type",
    },
    {
      name: "linkedEntityType",
      label: "Linked entity type",
      type: "select" as const,
      required: true,
      options: resolvedLinkedEntityTypes,
      placeholder: "Select linked entity type",
    },
    {
      name: "linkedEntityId",
      label: "Linked complaint",
      type: "select" as const,
      required: true,
      options: complaintOptions,
      placeholder:
        "Select linked complaint",
      help:
        complaintOptions.length === 0
          ? "No complaints are available."
          : "Select the complaint linked to this task.",
    },
    {
      name: "assignedToUserId",
      label: "Assign to user",
      type: "select" as const,
      options: userOptions,
      placeholder: "Select assign to user",
    },
    {
      name: "assignedRoleCode",
      label: "Assigned role code",
      type: "select" as const,
      options: resolvedRoles,
      placeholder: "Select assigned role",
    },
    {
      name: "ownerUserId",
      label: "Task owner",
      type: "select" as const,
      options: userOptions,
      placeholder: "Select task owner",
    },
    {
      name: "dueAtUtc",
      label: "Due date/time",
      type:
        "datetime-local" as const,
      required: true,
    },
    {
      name: "priorityCode",
      label: "Priority",
      type: "select" as const,
      required: true,
      options: resolvedPriorities,
      placeholder: "Select priority",
    },
    {
      name: "confidentiality",
      label: "Confidentiality",
      type: "select" as const,
      required: true,
      options: resolvedConfidentialities,
      placeholder: "Select confidentiality",
    },
  ];
}

function buildReassignFields(
  userOptions:
    readonly CmtsFormOption[],
): readonly CmtsFormField[] {
  return [
    {
      name: "assignedToUserId",
      label: "Target user",
      type: "select" as const,
      required: true,
      options: userOptions,
      placeholder: "Select target user",
    },
    {
      name: "reason",
      label: "Reassignment reason",
      type: "textarea" as const,
      required: true,
      minLength: 4,
    },
  ];
}

const commentFields:
  readonly CmtsFormField[] = [
  {
    name: "comment",
    label: "Task comment",
    type: "textarea" as const,
    required: true,
    minLength: 4,
  },
];

const snoozeFields:
  readonly CmtsFormField[] = [
  {
    name: "snoozeUntil",
    label: "Snooze until",
    type:
      "datetime-local" as const,
    required: true,
  },
  {
    name: "remarks",
    label: "Reason",
    type: "textarea" as const,
    required: true,
    minLength: 4,
  },
];

export function CmtsTasksPage(
  props: {
    tasks?:
    | readonly EnterpriseTask[]
    | undefined;

    ownerId?: string | undefined;

    diagnostics?:
    | readonly ComponentDiagnostic[]
    | undefined;

    busy?: boolean | undefined;

    error?:
    | string
    | null
    | undefined;

    onOpenTask: (
      task: EnterpriseTask,
    ) => void | Promise<void>;

    onCompleteTask: (
      task: EnterpriseTask,
      payload?:
        | Record<string, unknown>
        | undefined,
    ) => void | Promise<void>;

    onAcknowledgeTask: (
      task: EnterpriseTask,
    ) => void | Promise<void>;

    onReassignTask: (
      task: EnterpriseTask,
      payload:
        Record<string, unknown>,
    ) => void | Promise<void>;

    onCommentTask: (
      task: EnterpriseTask,
      payload:
        Record<string, unknown>,
    ) => void | Promise<void>;

    onSnoozeTask: (
      task: EnterpriseTask,
      payload:
        Record<string, unknown>,
    ) => void | Promise<void>;

    onCreateTask: (
      payload:
        Record<string, unknown>,
    ) => void | Promise<void>;

    taskCreateSupported?:
    | boolean
    | undefined;

    onOpenAuditTrail: (
      task: EnterpriseTask,
    ) => void | Promise<void>;

    onOpenOperationSpine: (
      task: EnterpriseTask,
    ) => void | Promise<void>;

    masterOptions?: {
      readonly priority?:
      | readonly CmtsFormOption[]
      | undefined;

      readonly users?:
      | readonly CmtsFormOption[]
      | undefined;

      readonly complaints?:
      | readonly CmtsFormOption[]
      | undefined;

      readonly taskTypes?: readonly CmtsFormOption[] | undefined;
      readonly linkedEntityTypes?: readonly CmtsFormOption[] | undefined;
      readonly roles?: readonly CmtsFormOption[] | undefined;
      readonly confidentiality?: readonly CmtsFormOption[] | undefined;
    };
  },
) {
  const [mode, setMode] =
    useState<ModalMode>(null);

  const [selected, setSelected] =
    useState<TaskRow | null>(null);

  const [
    actionsFor,
    setActionsFor,
  ] = useState<TaskRow | null>(
    null,
  );

  const rows = (
    props.tasks ?? []
  ) as readonly TaskRow[];

  const [searchParams] =
    useSearchParams();

  const initialGridState =
    useMemo(
      () =>
        initialGridStateFromUrl(
          searchParams,
        ),
      [searchParams],
    );

  const openModal = (
    nextMode: ModalMode,
    row?: Record<string, unknown>,
  ) => {
    setSelected(
      row
        ? row as TaskRow
        : null,
    );

    setActionsFor(null);
    setMode(nextMode);
  };

  const closeFormModal = () => {
    if (!props.busy) {
      setMode(null);
    }
  };

  const runAction = async (
    action:
      () => void | Promise<void>,
  ): Promise<void> => {
    try {
      await action();
      setActionsFor(null);
    } catch {
      /*
       * Container mutation error is
       * exposed through props.error.
       * Keep popup open on error.
       */
    }
  };
const linkedRecordLabel = (
  row: Record<string, unknown>,
): string => {
  const backendComplaintNumber =
    text(
      row,
      [
        "linkedRecordCode",
        "linkedEntityCode",
        "linkedEntityNumber",
        "sourceDisplayCode",
        "displayCode",
        "complaintNumber",
        "complaintCode",
        "referenceNumber",
        "sourceReferenceNumber",
        "parentComplaintNumber",
        "taskNumber",
      ],
    );

  if (backendComplaintNumber) {
    return backendComplaintNumber;
  }

  // Check sourceLinks array if present on DTO
  const sourceLinks = row.sourceLinks;
  if (Array.isArray(sourceLinks) && sourceLinks.length > 0) {
    const firstLink = sourceLinks[0] as Record<string, unknown>;
    const displayCode = text(firstLink, ["displayCode", "code", "entityNumber", "sourceCode"]);
    if (displayCode) return displayCode;
  }

  const id = linkedId(row);
  const complaintOptions =
    props.masterOptions?.complaints;

  if (id && complaintOptions && complaintOptions.length > 0) {
    const complaintOption =
      complaintOptions.find(
        (option) =>
          normalizeLinkedId(
            option.value,
          ) ===
          normalizeLinkedId(id),
      );

    if (complaintOption?.label) {
      return complaintOption.label;
    }
  }

  if (id) {
    if (id.length > 18) {
      return `Ref: ${id.substring(0, 8)}...`;
    }
    return id;
  }

  return props.busy ? "Loading..." : "-";
};
  const taskColumns:
    readonly CmtsGridColumn<
      Record<string, unknown>
    >[] = [
      {
        id: "title",
        header: "Task",
        sortable: true,
        filterable: true,
        minWidth: 240,

        render: (row) => (
          <div className="cmts-task-title-cell">
            <strong>
              {text(row, ["title", "summary"], "Workflow task")}
            </strong>

            <small>
              {text(
                row,
                ["taskTypeName", "taskTypeCode"],
                "General",
              )}
            </small>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        filterable: true,
        minWidth: 120,

        render: (row) => (
          <span
            className={[
              "cmts-status-chip",
              taskStatusClass(taskStatus(row)),
            ].join(" ")}
          >
            {taskStatus(row).replace(/_/g, " ")}
          </span>
        ),
      },
      {
        id: "priority",
        header: "Priority",
        sortable: true,
        filterable: true,
        minWidth: 110,

        render: (row) => {
          const priority = text(
            row,
            ["priority"],
            "medium",
          );

          return (
            <span
              className={[
                "cmts-priority-chip",
                `cmts-priority-chip--${priority.toLowerCase()}`,
              ].join(" ")}
            >
              {priority}
            </span>
          );
        },
      },
      {
        id: "ownerId",
        header: "Owner",
        sortable: true,
        filterable: true,
        minWidth: 150,

        render: (row) =>
          text(
            row,
            [
              "assignedToUserName",
              "ownerName",
              "ownerId",
              "assignedToUserId",
            ],
            "Unassigned",
          ),
      },
      {
        id: "dueAt",
        header: "Due",
        sortable: true,
        filterable: true,
        minWidth: 165,

        render: (row) =>
          formatTaskDate(
            row,
            [
              "dueAt",
              "dueAtUtc",
              "dueDate",
              "dueDateUtc",
              "targetDate",
              "targetDateUtc",
              "slaDueUtc",
              "deadline",
              "scheduledAtUtc",
            ],
          ),
      },
      {
        id: "linkedEntityType",
        header: "Source",
        sortable: true,
        filterable: true,
        minWidth: 140,

        render: (row) =>
          text(
            row,
            [
              "linkedEntityType",
              "sourceModule",
              "entityType",
            ],
            "COMPLAINT",
          ),
      },
     {
  id: "complaintId",
  header: "Linked Record",
  sortable: true,
  filterable: true,
  minWidth: 240,

  render: (row) => {
    const label =
      linkedRecordLabel(
        row,
      );

    const rawType =
      text(
        row,
        [
          "linkedEntityType",
          "sourceModule",
          "entityType",
        ],
        "Complaint",
      ).trim();

    return (
      <div className="cmts-task-linked-record">
        <strong>
          {label}
        </strong>

        {label !== "-" &&
          label !== "Loading..." && (
          <small>
            {rawType}
          </small>
        )}
      </div>
    );
  },
},
      {
        id: "taskActions",
        header: "Actions",
        sortable: false,
        filterable: false,
        minWidth: 125,

        render: (row) => (
          <button
            type="button"
            className="cmts-notification-actions-button"
            aria-label="Open task actions"
            onClick={(event) => {
              event.stopPropagation();

              setActionsFor(
                row as TaskRow,
              );
            }}
          >
            <span
              className="cmts-notification-actions-button__icon"
              aria-hidden="true"
            >
              ⋮
            </span>

            <span>Actions</span>
          </button>
        ),
      },
    ];

  const createFields =
    useMemo<readonly CmtsFormField[]>(
      () =>
        buildCreateFields(
          props.masterOptions
            ?.priority ?? [],
          props.masterOptions
            ?.users ?? [],
          props.masterOptions
            ?.complaints ?? [],
          props.masterOptions?.taskTypes ?? [],
          props.masterOptions?.linkedEntityTypes ?? [],
          props.masterOptions?.roles ?? [],
          props.masterOptions?.confidentiality ?? [],
        ),
      [
        props.masterOptions
          ?.priority,
        props.masterOptions
          ?.users,
        props.masterOptions
          ?.complaints,
        props.masterOptions?.taskTypes,
        props.masterOptions?.linkedEntityTypes,
        props.masterOptions?.roles,
        props.masterOptions?.confidentiality,
      ],
    );

  const reassignFields =
    useMemo<readonly CmtsFormField[]>(
      () =>
        buildReassignFields(
          props.masterOptions
            ?.users ?? [],
        ),
      [
        props.masterOptions
          ?.users,
      ],
    );

  const modalTitle =
    mode === "create"
      ? "Create task"
      : mode === "reassign"
        ? "Reassign task"
        : mode === "comment"
          ? "Add task comment"
          : "Snooze task";

  const modalPresentation = {
    create: {
      eyebrow: "TASK MANAGEMENT",
      title: "Create a new task",
      description:
        "Create, assign and link a governed task to a complaint.",
      sectionTitle: "Task information",
      sectionDescription:
        "Provide task, complaint and ownership details.",
      badge: "Backend validated",
    },
    reassign: {
      eyebrow: "OWNERSHIP MANAGEMENT",
      title: "Reassign task ownership",
      description:
        "Select a new assignee and safely transfer responsibility for this task.",
      sectionTitle: "Reassignment details",
      sectionDescription:
        "Select the new task owner and provide the required information.",
      badge: "Audited action",
    },
    comment: {
      eyebrow: "TASK COLLABORATION",
      title: "Add a task comment",
      description:
        "Add a clear update that will be stored in the task activity history.",
      sectionTitle: "Comment details",
      sectionDescription:
        "Enter the update or information that should be shared with the task team.",
      badge: "Activity recorded",
    },
    snooze: {
      eyebrow: "TASK SCHEDULING",
      title: "Snooze this task",
      description:
        "Temporarily pause this task until the selected date and time.",
      sectionTitle: "Snooze details",
      sectionDescription:
        "Choose when the task should return and provide a reason.",
      badge: "Reminder enabled",
    },
  } as const;

  const activeModalPresentation =
    mode !== null
      ? modalPresentation[mode]
      : modalPresentation.create;

  const modalFields:
    readonly CmtsFormField[] =
    mode === "create"
      ? createFields
      : mode === "reassign"
        ? reassignFields
        : mode === "comment"
          ? commentFields
          : snoozeFields;

  const selectedClosed =
    actionsFor !== null &&
    isClosed(actionsFor);

  const selectedCanAcknowledge =
    actionsFor !== null &&
    !selectedClosed &&
    capability(
      actionsFor,
      "canAcknowledge",
    );

  const selectedCanComplete =
    actionsFor !== null &&
    !selectedClosed &&
    capability(
      actionsFor,
      "canComplete",
    );

  const selectedCanAssign =
    actionsFor !== null &&
    !selectedClosed &&
    capability(
      actionsFor,
      "canAssign",
      false,
    );

  const selectedCanComment =
    actionsFor !== null &&
    capability(
      actionsFor,
      "canComment",
    );

  const selectedCanSnooze =
    actionsFor !== null &&
    !selectedClosed &&
    capability(
      actionsFor,
      "canSnooze",
    );

  return (
    <PageContainer
      title="My Tasks"
      subtitle="Repository-backed task queue for complaint, RCA, CAPA, verification, SLA, escalation and governance work."
    >
      <CmtsPageHintPanel defaultOpen>
        Tasks persist through the
        backend repository and are
        linked to workflow history,
        audit events, operation spine
        and their source records.
      </CmtsPageHintPanel>

      <section className="cmts-page-action-bar">
        <div className="cmts-page-actions">
          <Button
            className="cmts-action-button"
            intent="button"
            disabled={
              props.busy ||
              props.taskCreateSupported ===
              false
            }
            title={
              props.taskCreateSupported ===
                false
                ? "Task create is not exposed by the current backend route contract."
                : undefined
            }
            onClick={() =>
              openModal("create")
            }
          >
            <FiPlusCircle />
            Create task
          </Button>
        </div>
      </section>

      <CmtsSmartDataGrid<
        Record<string, unknown>
      >
        title="My task queue"
        subtitle="Search, filter and manage assigned workflow tasks."
        rows={rows}
        columns={taskColumns}
        filters={taskFilters}
        initialFilterValues={
          initialGridState.filters
        }
        initialSearchText={
          initialGridState.searchText
        }
        rowKey="id"
        defaultPageSize={10}
        searchable
        sortable
        filterable
        exportable
        loading={props.busy}
        error={props.error}
        filterSummary={[
          "Tenant/persona scope applied",
          "Task actions write audit and operation-spine events",
        ]}
        emptyMessage="No open tasks are assigned to this persona."
      />

      {/* Task actions popup */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Task actions"
        subtitle={
          actionsFor
            ? text(
              actionsFor,
              ["title", "summary"],
              `Task ${taskId(
                actionsFor,
              )}`,
            )
            : ""
        }
        size="md"
        onClose={() => {
          if (!props.busy) {
            setActionsFor(null);
          }
        }}
      >
        {actionsFor && (
          <div className="cmts-notification-action-modal">
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>
                  Current status
                </span>

                <strong>
                  {taskStatus(
                    actionsFor,
                  )}
                </strong>
              </div>

              <div>
                <span>Priority</span>

                <strong>
                  {text(
                    actionsFor,
                    ["priority"],
                    "MEDIUM",
                  ).toUpperCase()}
                </strong>
              </div>

              <div>
                <span>Due</span>

                <strong>
                  {formatTaskDate(
                    actionsFor,
                    [
                      "dueAt",
                      "dueAtUtc",
                      "dueDate",
                    ],
                  )}
                </strong>
              </div>
            </div>

            {props.error && (
              <div
                className="cmts-notification-action-modal__error"
                role="alert"
              >
                {props.error}
              </div>
            )}

            <div className="cmts-notification-action-modal__grid">
              <TaskActionCard
                icon="↗"
                title="Open linked record"
                description="Open the linked complaint, RCA, CAPA or verification context."
                disabled={
                  props.busy ||
                  !linkedId(actionsFor)
                }
                onClick={() => {
                  const task =
                    actionsFor;

                  void runAction(() =>
                    props.onOpenTask(
                      task,
                    ),
                  );
                }}
              />

              <TaskActionCard
                icon="✓"
                title="Acknowledge task"
                description="Confirm that this task has been received and reviewed."
                disabled={
                  props.busy ||
                  !selectedCanAcknowledge
                }
                onClick={() => {
                  const task =
                    actionsFor;

                  void runAction(() =>
                    props.onAcknowledgeTask(
                      task,
                    ),
                  );
                }}
              />

              <TaskActionCard
                icon="★"
                title="Complete task"
                description="Mark this task as completed with an audit entry."
                disabled={
                  props.busy ||
                  !selectedCanComplete
                }
                onClick={() => {
                  const task =
                    actionsFor;

                  void runAction(() =>
                    props.onCompleteTask(
                      task,
                      {
                        remarks:
                          `Completed task ${taskId(
                            task,
                          )} from My Work.`,
                      },
                    ),
                  );
                }}
              />

              <TaskActionCard
                icon="⇄"
                title="Reassign task"
                description="Assign this task to another permitted user."
                disabled={
                  props.busy ||
                  !selectedCanAssign
                }
                onClick={() => {
                  openModal(
                    "reassign",
                    actionsFor,
                  );
                }}
              />

              <TaskActionCard
                icon="+"
                title="Add comment"
                description="Add an audit-safe comment to this task."
                disabled={
                  props.busy ||
                  !selectedCanComment
                }
                onClick={() => {
                  openModal(
                    "comment",
                    actionsFor,
                  );
                }}
              />

              <TaskActionCard
                icon="◷"
                title="Snooze task"
                description="Pause this task until a selected date and time."
                disabled={
                  props.busy ||
                  !selectedCanSnooze
                }
                onClick={() => {
                  openModal(
                    "snooze",
                    actionsFor,
                  );
                }}
              />

              <TaskActionCard
                icon="◉"
                title="Open audit trail"
                description="Review task history and its audit events."
                disabled={props.busy}
                onClick={() => {
                  const task =
                    actionsFor;

                  void runAction(() =>
                    props.onOpenAuditTrail(
                      task,
                    ),
                  );
                }}
              />

              <TaskActionCard
                icon="⌁"
                title="Operation spine"
                description="Open linked workflow and processing evidence."
                disabled={props.busy}
                onClick={() => {
                  const task =
                    actionsFor;

                  void runAction(() =>
                    props.onOpenOperationSpine(
                      task,
                    ),
                  );
                }}
              />
            </div>

            {selectedClosed && (
              <div className="cmts-notification-action-modal__danger">
                <div
                  className="cmts-notification-action-option cmts-notification-action-option--danger"
                  aria-disabled="true"
                >
                  <span
                    className="cmts-notification-action-option__icon"
                    aria-hidden="true"
                  >
                    ✓
                  </span>

                  <span className="cmts-notification-action-option__content">
                    <strong>
                      Task lifecycle closed
                    </strong>

                    <small>
                      This task is already
                      completed, cancelled,
                      archived or closed.
                    </small>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </CmtsEnterpriseModal>

      {/* Create/Reassign/Comment/Snooze form */}
<CmtsEnterpriseModal
  open={mode !== null}
  title={modalTitle}
  subtitle={
    mode === "create"
      ? undefined
      : selected
        ? `${text(
            selected,
            [
              "title",
              "summary",
            ],
            "Selected task",
          )} • ${taskId(
            selected,
          )}`
        : "Changes are persisted through the backend task repository."
  }
  onClose={
    closeFormModal
  }
  size="lg"
>
  <div
   className={[
  "cmts-task-form-modal",
  mode
    ? `cmts-task-form-modal--${mode}`
    : "",
]
  .filter(Boolean)
  .join(" ")}
  >
   {mode !== null && (
  <>
    <section className="cmts-task-form-hero">
      <div className="cmts-task-form-hero__icon">
        <FiPlusCircle />
      </div>

      <div className="cmts-task-form-hero__content">
        <span className="cmts-task-form-hero__eyebrow">
          {activeModalPresentation.eyebrow}
        </span>

        <h3>
          {activeModalPresentation.title}
        </h3>

        <p>
          {activeModalPresentation.description}
        </p>
      </div>

      <span className="cmts-task-form-hero__status">
        <span />
        {activeModalPresentation.badge}
      </span>
    </section>

    {mode === "create" ? (
      <div className="cmts-task-form-steps">
        <div className="cmts-task-form-step">
          <span>1</span>

          <div>
            <strong>Task details</strong>
            <small>Title and description</small>
          </div>
        </div>

        <div className="cmts-task-form-step">
          <span>2</span>

          <div>
            <strong>Link and classify</strong>
            <small>Complaint and priority</small>
          </div>
        </div>

        <div className="cmts-task-form-step">
          <span>3</span>

          <div>
            <strong>Assign ownership</strong>
            <small>Assignee, owner and due</small>
          </div>
        </div>
      </div>
    ) : selected ? (
      <div className="cmts-task-selected-summary">
        <div>
          <span>Selected task</span>

          <strong>
            {text(
              selected,
              ["title", "summary"],
              "Workflow task",
            )}
          </strong>
        </div>

        <div>
          <span>Current status</span>

          <strong
            className={[
              "cmts-status-chip",
              taskStatusClass(taskStatus(selected)),
            ].join(" ")}
          >
            {taskStatus(selected).replace(/_/g, " ")}
          </strong>
        </div>
      </div>
    ) : null}

    <section className="cmts-task-form-section-heading">
      <span>01</span>

      <div>
        <strong>
          {activeModalPresentation.sectionTitle}
        </strong>

        <small>
          {activeModalPresentation.sectionDescription}
        </small>
      </div>

      <em>Required fields</em>
    </section>
  </>
)}
    <CmtsEnterpriseForm
      fields={
        modalFields
      }
      initialValues={
        mode === "create"
          ? {
              taskTypeCode:
                "GENERAL",

              linkedEntityType:
                "Complaint",

              priorityCode:
                "MEDIUM",

              confidentiality:
                "INTERNAL",
            }
          : selected ?? {}
      }
      submitLabel={
        mode === "create"
          ? "Create task"
          : mode ===
                "reassign"
            ? "Reassign"
            : mode ===
                  "comment"
              ? "Add comment"
              : "Snooze"
      }
      error={
        props.error
      }
      busy={
        props.busy
      }
      onCancel={
        closeFormModal
      }
      onSubmit={async (
        payload,
      ) => {
        if (
          mode === "create"
        ) {
          await props.onCreateTask({
            ...payload,

            linkedEntityType:
              "Complaint",

            parentComplaintId:
              payload.linkedEntityId,
          });
        } else if (
          selected &&
          mode === "reassign"
        ) {
          await props.onReassignTask(
            selected,
            payload,
          );
        } else if (
          selected &&
          mode === "comment"
        ) {
          await props.onCommentTask(
            selected,
            payload,
          );
        } else if (
          selected &&
          mode === "snooze"
        ) {
          await props.onSnoozeTask(
            selected,
            payload,
          );
        }

        setMode(null);
      }}
    />
  </div>
</CmtsEnterpriseModal>
    </PageContainer>
  );
}